import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { v1Client } from '../api'
import { useAuth } from '../providers'
import { isTurnLive, beginTurn, endTurn, getTurnStartTime, isTurnRegistered } from '../store'
import { useSubscribeToChat } from './use-subscribe-to-chat'
import { staleVerifyDropped, type StaleVerifyDropped } from '../engine/errors'
import { reportTurnError, traceTurnEvent } from '../engine/telemetry'
import { describeCause, type TurnTraceEvent } from '../engine/turn-trace'

const STREAMING_STATE_GC_MS = 5 * 60_000

/**
 * Verifies and synchronises streaming UI state with the backend when a chat
 * becomes active (e.g. drawer opens / chat ID changes).
 *
 * One verify per drawer open, deduped via React Query's inflight handling.
 * Side-effects are split into a data-driven effect with subscribe captured in
 * a ref so identity churn from re-renders during streaming doesn't retrigger
 * the verify.
 *
 * When the backend reports streaming, re-subscribes via `subscribe()`. The
 * atom keeps any prior content; replayed SSE deltas extend it via the
 * atom-seeded accumulator in useSubscribeToChat. (No optimistic restore is
 * needed while the verify is in flight — the per-chat turn status survives
 * drawer close/reopen on its own.)
 */
/**
 * @param skipVerify - Suppress the backend verify + re-subscribe. Set when the
 *   drawer was opened by a fresh local submit (e.g. editing a staged action),
 *   where `submitMessage`'s POST stream already owns this conversation. Without
 *   this, the verify resolves `isStreaming: true` and opens a competing
 *   `/subscribe` stream that clobbers the in-flight submit (lost staged-action
 *   preview + reset loading state). New chats avoid this via `isNewChat`.
 */
export function useSyncStreamingState(chatId: string | undefined, isNewChat: boolean, skipVerify = false) {
  const { getAuthToken } = useAuth()
  const { subscribe, unsubscribe } = useSubscribeToChat()
  const subscribeAttemptedRef = useRef<string | undefined>()
  // The verify effect re-runs on drawer remounts while cached data lives;
  // latch per chat+reason so one logical drop reports once, not per render.
  const staleDropReportedRef = useRef<string | undefined>()

  // Capture the latest subscribe in a ref so the side-effect below doesn't
  // re-fire whenever subscribe's identity changes (it has a long dep list).
  const subscribeRef = useRef(subscribe)
  useEffect(() => {
    subscribeRef.current = subscribe
  }, [subscribe])

  const { data } = useQuery({
    queryKey: ['conversation-streaming-state', chatId],
    queryFn: async () => {
      // Captured before the request: a local turn that began at or after this
      // instant is fresher than whatever the verify reports.
      const verifyStartedAt = Date.now()
      const token = await getAuthToken()
      const response = await v1Client.GET('/conversations/{id}', {
        params: { path: { id: chatId! } },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      // A failed verify leaves a mid-turn chat looking idle with nothing to
      // resume it. It is the one silent path in the resume flow.
      if (response.error) {
        traceTurnEvent({
          kind: 'verify:failed',
          chatId: chatId!,
          status: response.response?.status,
          cause: describeCause(response.error),
        })
      }
      const conversation = response.data?.data
      return conversation ? { ...conversation, verifyStartedAt } : null
    },
    enabled: !!chatId && !isNewChat && !skipVerify,
    // Always re-verify on drawer open: the backend stream may have ended while
    // the drawer was closed, so cached data could be a lie. Inflight dedupe
    // still handles double-mounts in a single tick.
    staleTime: 0,
    gcTime: STREAMING_STATE_GC_MS,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    // A reconnect during a normal edit/stream would flip data.isStreaming and
    // chain into refetchQueries(['messages']), clobbering the in-flight user
    // message + assistant placeholder. The drawer-open refetch is enough.
    refetchOnReconnect: false,
  })

  useEffect(() => {
    // skipVerify also gates here, not just `enabled`: with enabled:false,
    // useQuery still returns previously-cached data for this queryKey within
    // gcTime (e.g. the chat was opened from history minutes ago), which would
    // otherwise flow through and clear/re-subscribe — the exact bug we suppress.
    if (!chatId || isNewChat || !data) return

    const reportStaleDropOnce = (reason: StaleVerifyDropped['reason']) => {
      const key = `${chatId}:${reason}`
      if (staleDropReportedRef.current === key) return
      staleDropReportedRef.current = key
      reportTurnError(staleVerifyDropped(chatId, reason))
    }

    if (skipVerify) {
      // The drawer was opened by a fresh local submit whose POST stream owns
      // this conversation — applying the verify would clobber it. Typed +
      // logged instead of a silent return.
      reportStaleDropOnce('fresh-submit-owns-chat')
      return
    }
    const turnLive = isTurnLive(chatId)
    const turnRegistered = isTurnRegistered(chatId)
    const traceVerify = (action: Extract<TurnTraceEvent, { kind: 'verify:result' }>['action']) => {
      traceTurnEvent({ kind: 'verify:result', chatId, isStreaming: data.isStreaming, turnLive, turnRegistered, action })
    }
    if (!data.isStreaming) {
      // Only finalise a live turn that is genuinely stale: one no engine
      // stream currently owns AND that began before the verify fired. A fresh
      // submission started after the request was in flight looks live locally
      // (its POST stream registers a turn, or — during context/attachment
      // resolution — has only begun the store-level turn), and this verify's
      // `isStreaming: false` predates it; ending it would clear a healthy
      // turn's status while its stream continues.
      const turnStart = getTurnStartTime(chatId)
      const freshLocalTurn = turnRegistered || (turnStart !== null && turnStart >= data.verifyStartedAt)
      if (turnLive) {
        if (freshLocalTurn) {
          traceVerify('keep-local-turn')
          reportStaleDropOnce('fresh-submit-owns-chat')
        } else {
          traceVerify('end-stale-turn')
          endTurn(chatId)
        }
      }
      // No live turn: opening a finished conversation — nothing to end and
      // nothing dropped, so no stale-verify report.
      return
    }

    // A live stream already owns this chat's turn. Subscribing would
    // supersede it through the registry and re-fold the backend's replay onto
    // text that stream already delivered, doubling it. The reopen path below
    // still runs: a turn aborted at drawer close leaves the registry within
    // microtasks, well before a reopen's verify can land.
    if (turnRegistered) {
      traceVerify('keep-local-turn')
      reportStaleDropOnce('live-turn-owns-chat')
      return
    }

    // Don't gate on isTurnLive: when the drawer was just closed
    // and reopened, the previous SSE loop's finally block may not have
    // cleared that flag yet (it runs as a microtask after the abort
    // propagates). The SSE loop token-guards its own cleanup so a stale
    // finally can't clobber a fresh subscription.
    if (subscribeAttemptedRef.current === chatId) {
      traceVerify('noop')
      return
    }
    subscribeAttemptedRef.current = chatId
    traceVerify('subscribe')

    // Seed the turn with the conversation title and the backend's turn start
    // before subscribe. subscribe()'s begin is title-less, so without this the
    // chat gets a live turn but no progress cell; the explicit start time
    // keeps elapsed displays accurate across drawer close/reopen.
    beginTurn(chatId, {
      ...(data.title ? { title: data.title } : {}),
      startTime: new Date(data.updatedAt).getTime(),
    })
    subscribeRef.current(chatId)

    return () => {
      // Allow a future effect run for the same chatId to re-attempt subscribe
      // if isStreaming flips false then true again (e.g. a follow-up stream
      // after the current one ends).
      if (subscribeAttemptedRef.current === chatId) {
        subscribeAttemptedRef.current = undefined
      }
    }
  }, [chatId, isNewChat, skipVerify, data?.isStreaming, data?.updatedAt, data?.title, data?.verifyStartedAt])

  return { unsubscribe }
}
